import { useCallback, useRef } from 'react';
import { usePlaybackStore } from '@/store/playbackStore.ts';
import { useUiStore } from '@/store/uiStore.ts';
import { useProjectStore } from '@/store/projectStore.ts';
import styles from './Playhead.module.css';

interface PlayheadProps {
  height: number;
}

export function Playhead({ height }: PlayheadProps) {
  const currentTime = usePlaybackStore(s => s.currentTime);
  const pixelsPerSecond = useUiStore(s => s.pixelsPerSecond);
  const seek = usePlaybackStore(s => s.seek);
  const pause = usePlaybackStore(s => s.pause);
  const getViewOffset = useProjectStore(s => s.getViewOffset);
  useProjectStore(s => s.activeSceneId); // re-render trigger for scene changes

  const dragRef = useRef<{ offsetX: number } | null>(null);

  const viewOffset = getViewOffset();
  const x = (currentTime - viewOffset) * pixelsPerSecond;

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    e.stopPropagation();
    pause();
    dragRef.current = { offsetX: 0 };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, [pause]);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragRef.current) return;
    // Get the position relative to the lane inner container
    const container = (e.target as HTMLElement).closest('[class*="laneInner"], [class*="rulerInner"]');
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const localX = e.clientX - rect.left;
    seek(localX / pixelsPerSecond + viewOffset);
  }, [pixelsPerSecond, seek, viewOffset]);

  const onPointerUp = useCallback(() => {
    dragRef.current = null;
  }, []);

  return (
    <div
      className={styles.playhead}
      style={{ height, transform: `translateX(${x}px)` }}
    >
      <div
        className={styles.handle}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      />
    </div>
  );
}
